import { type Context, Controller } from '@hotwired/stimulus';
import * as d3 from 'd3';

const DEBUG_VORONOI = false;

export default class GraphsPerformanceQqController extends Controller {
  static targets = ['graph', 'table', 'context'];

  public static values = {
    data: Array,
    dataMin: Number,
    dataMax: Number,
    width: Number,
    height: Number,
    graphLeft: Number,
    graphTop: Number,
    identifier: String,
    score: Number,
    fontSize: Number,
    textHeight: Number,
  };

  private declare graphTarget: SVGSVGElement;
  private declare tableTarget: HTMLTableElement;
  private declare contextTarget: SVGTextElement;

  private declare readonly dataValue: {
    percentile: string;
    in_value: number;
    out_value: number;
    in_label: string;
    out_label: string;
  }[];
  private declare readonly dataMinValue: number;
  private declare readonly dataMaxValue: number;
  private declare readonly widthValue: number;
  private declare readonly heightValue: number;
  private declare readonly graphLeftValue: number;
  private declare readonly graphTopValue: number;
  private declare readonly identifierValue: string;
  private declare readonly scoreValue: number;
  private declare readonly fontSizeValue: number;
  private declare readonly textHeightValue: number;

  private declare readonly hasScoreValue: boolean;

  public constructor(context: Context) {
    super(context);
  }

  public connect(): void {
    // Declare the chart dimensions and margins.
    const width = this.widthValue;
    const height = this.heightValue;
    const marginRight = 0;
    const marginBottom = 0;
    const marginTop = this.graphTopValue;
    const marginLeft = this.graphLeftValue;

    const data = this.dataValue.filter(
      (d) =>
        isFinite(d.in_value) &&
        isFinite(d.out_value) &&
        d.in_value > 0 &&
        d.out_value > 0,
    ); // <%== data.to_json %>

    // No data
    if (data.length === 0) {
      return;
    }

    const min = Math.max(0.0001, this.dataMinValue);
    const max = this.dataMaxValue;

    // Invalid data
    if (min > max) {
      return;
    }

    const x = d3
      .scaleLog([min, max], [marginLeft, width - marginRight])
      .base(2);
    const y = d3
      .scaleLog([min, max], [height - marginBottom, marginTop])
      .base(2);

    const coords = {
      topLeft: [x(min), y(max)],
      topRight: [x(max), y(max)],
      bottomLeft: [x(min), y(min)],
      bottomRight: [x(max), y(min)],
      width: x(max) - x(min),
      height: y(min) - y(max),
    };

    // Declare the line generator.
    const line = d3
      .line<(typeof data)[number]>()
      // .defined((d) => isFinite(d.in_value) && isFinite(d.out_value))
      .defined(function (d) {
        return (
          d.in_value <= max * 2 &&
          d.out_value <= max * 2 &&
          d.in_value >= min / 2 &&
          d.out_value >= min / 2
        );
      })
      .x((d) => x(d.in_value))
      .y((d) => y(d.out_value));

    this.graphTarget.setAttribute('hidden', '');

    // Create the SVG container.
    const svg = d3
      .create('svg')
      .attr('data-component', this.graphTarget.getAttribute('data-component'))
      .attr('class', this.graphTarget.getAttribute('class'))
      .classed('--interactive', true)
      .attr('viewBox', [0, 0, width, height])
      .attr('style', 'font: 10px sans-serif;')
      .style('-webkit-tap-highlight-color', 'transparent')
      .style('overflow', 'visible')
      .attr('aria-hidden', 'true')
      .on('pointerenter pointermove', onPointerMoved)
      .on('pointerleave', onPointerLeft)
      .on('touchstart', (event) => event.preventDefault());

    // Add clipping path for the line & axis
    svg
      .append('clipPath')
      .attr('id', `clip-${this.identifierValue}`)
      .append('rect')
      .attr('x', coords.topLeft[0])
      .attr('y', coords.topLeft[1])
      .attr('width', coords.width)
      .attr('height', coords.height);

    svg
      .append('clipPath')
      .attr('id', `area-negative-${this.identifierValue}`)
      .append('polygon')
      .attr('class', 'area-negative')
      .attr(
        'points',
        `${coords.bottomLeft.join(',')}, ${coords.topRight.join(',')}, ${coords.bottomRight.join(',')}`,
      );

    svg
      .append('clipPath')
      .attr('id', `area-positive-${this.identifierValue}`)
      .append('polygon')
      .attr('class', 'area-negative')
      .attr(
        'points',
        `${coords.topLeft.join(',')}, ${coords.topRight.join(',')}, ${coords.bottomLeft.join(',')}`,
      );

    // Add the (green) polygon for positive values
    svg
      .append('polygon')
      .attr('class', 'area-positive')
      .attr(
        'points',
        `${coords.topLeft.join(',')}, ${coords.topRight.join(',')}, ${coords.bottomLeft.join(',')}`,
      );

    // Add the (red) polygon for negative values
    svg
      .append('polygon')
      .attr('id', `area-negative-${this.identifierValue}`)
      .attr('class', 'area-negative')
      .attr(
        'points',
        `${coords.bottomLeft.join(',')}, ${coords.topRight.join(',')}, ${coords.bottomRight.join(',')}`,
      );

    // Add the axis for the values
    const labels = [
      { value: 3600, label: false /* 1h */ },
      { value: 7200, label: '2h' },
      { value: 28800, label: '8h' },
      { value: 86400, label: false /* 24 hours */ },
      { value: 172800, label: '2d' },
      { value: 604800, label: '7d' },
      { value: 1209600, label: false /* 2 weeks */ },
      { value: 2592000, label: '30d' },
      { value: 5184000, label: false /* 2 months */ },
      { value: 15768000, label: '0.5y' },
      { value: 31536000, label: '1y' },
      { value: 63072000, label: false /* 2 years */ },
    ];

    // Add guide lines (bottom to center)
    svg
      .append('g')
      .attr('clip-path', `url(#${`area-negative-${this.identifierValue}`})`)
      .append('g')
      .attr('transform', `translate(0, ${height - marginBottom})`)
      .call(
        d3
          .axisTop(x)
          .tickSize(height - marginTop - marginBottom)
          .tickValues(labels.map((l) => l.value))
          .tickFormat(() => ''),
      )
      .call((g) => {
        g.select('.domain').remove();
        g.select('.tick:first-of-type').remove();
        g.select('.tick:last-of-type').remove();

        // Add guidelines
        g.selectAll('.tick line')
          .attr('stroke-opacity', 0.2)
          .attr('stroke-dasharray', '2,2');
        g.selectAll('.tick').classed('guide', true);
      });

    // Add guide lines (left to center)
    if (height >= 100) {
      svg
        .append('g')
        .attr('clip-path', `url(#${`area-positive-${this.identifierValue}`})`)
        .append('g')
        .attr('transform', `translate(${width}, 0)`)
        .call(
          d3
            .axisLeft(y)
            .tickSize(width - marginLeft - marginRight)
            .tickValues(labels.map((l) => l.value))
            .tickFormat(() => ''),
        )
        .call((g) => {
          g.select('.domain').remove();
          g.select('.tick:first-of-type').remove();
          g.select('.tick:last-of-type').remove();

          // Add guidelines
          g.selectAll('.tick line')
            .attr('stroke-opacity', 0.2)
            .attr('stroke-dasharray', '2,2');
          g.selectAll('.tick').classed('guide', true);
        });
    }

    // Add axis at bottom
    svg
      .append('g')
      .attr('transform', `translate(0, ${height - marginBottom})`)
      .call(
        d3
          .axisTop(x)
          .tickValues(labels.map((l) => l.value))
          .tickFormat((d): string => {
            const label = labels.find(
              ({ value }) => value === Number(d),
            )?.label;
            return typeof label === 'string' ? label : '';
          }),
      )
      .call((g) => {
        g.select('.domain').remove();
        g.select('.tick:first-of-type').remove();
        g.select('.tick:last-of-type').remove();

        // Mark as guides if small graph
        if (height < 100) {
          g.selectAll('.tick').classed('guide', true);
        }
      });

    // Add score
    if (this.hasScoreValue) {
      svg
        .append('text')
        .attr('x', marginLeft / 2)
        .attr('y', height / 2 - this.textHeightValue)
        .attr('text-anchor', 'middle')
        .attr('dominant-baseline', 'middle')
        .attr('font-size', this.fontSizeValue)
        .classed('score-label', true)
        .text('Score:');

      svg
        .append('text')
        .attr('x', marginLeft / 2)
        .attr('y', height / 2 + this.textHeightValue)
        .attr('text-anchor', 'middle')
        .attr('dominant-baseline', 'middle')
        .attr('font-size', this.fontSizeValue)
        .classed('score', true)
        .classed(
          this.scoreValue < 0 ? 'score-bad --bad' : 'score-good --good',
          true,
        )
        .text(isNaN(this.scoreValue) ? '-' : this.scoreValue);
    }

    // Append a path for the line.
    svg
      .append('path')
      .attr('fill', 'none')
      .attr('stroke', 'currentColor')
      .attr('stroke-width', 1.5)
      .attr('d', line(data))
      .attr('clip-path', `url(#${`clip-${this.identifierValue}`})`);

    // Add graph area
    const graphArea = svg
      .append('g')
      .attr('transform', `translate(${marginLeft}, ${marginTop})`);
    // .attr('clip-path', `url(#${`clip-${this.identifierValue}`})`);

    // Run voronoi calculation for mouse over
    const voronoiData = data
      .map(
        (datum) =>
          [
            x(datum.in_value) - marginLeft,
            y(datum.out_value) - marginTop,
            datum,
          ] as [number, number, (typeof data)[number]],
      )
      .filter(
        (d) =>
          isFinite(d[0]) &&
          isFinite(d[1]) &&
          0 <= d[0] &&
          d[0] <= coords.width &&
          0 <= d[1] &&
          d[1] <= coords.height,
      );

    const delaunay = d3.Delaunay.from(voronoiData.map(([x, y]) => [x, y]));
    const voronoi = delaunay.voronoi([
      0, // coords.topLeft[0],
      0, // coords.topLeft[1],
      coords.width, // coords.bottomRight[0],
      coords.height, // coords.bottomRight[1],
    ]);

    const cells = voronoiData.map(
      ([x, y, datum], i) =>
        [[x, y], voronoi.cellPolygon(i), i, datum] as [
          [number, number],
          ReturnType<(typeof voronoi)['cellPolygon']>,
          number,
          (typeof data)[number],
        ],
    );

    // Debug the voronoi rendering:
    if (DEBUG_VORONOI) {
      graphArea
        .append('g')
        .attr('id', `debug-voronoi-${this.identifierValue}`)
        .attr('stroke', 'orange')
        .selectAll('path')
        .data(cells)
        .join('path')
        .attr('d', ([d, cell, _i]) => `M${d3.polygonCentroid(cell)}L${d}`);

      graphArea
        .append('path')
        .attr('id', `debug-voronoi-render-${this.identifierValue}`)
        .attr('fill', 'none')
        .attr('stroke', '#ccc')
        .attr('d', voronoi.render());

      graphArea
        .append('path')
        .attr('id', `debug-voronoi-points-${this.identifierValue}`)
        .attr('d', delaunay.renderPoints(undefined, 2));
    } else {
      graphArea
        .append('g')
        .attr('id', `voronoi-hover-map-${this.identifierValue}`)
        .attr('stroke', 'orange')
        .selectAll('path')
        .data(cells)
        .join('path')
        .attr('d', (_, i) => voronoi.renderCell(i))
        .attr('pointer-events', 'all')
        .style('opacity', 0)
        .on('pointerenter pointermove', (event, [d, _cell, i, datum]) => {
          if (!event.isPrimary) {
            return;
          }

          // Already active
          if (tooltip.attr('data-index') === String(i)) {
            return;
          }

          tooltip.style('display', null).attr('data-index', String(i));

          graphArea.selectAll(`circle:not([data-index="${i}"])`).attr('r', 0);
          graphArea.select(`circle[data-index="${i}"]`).attr('r', 3);

          const bubble = tooltip
            .selectAll('rect')
            .data([undefined, undefined])
            .join('rect')
            .attr('fill', 'rgba(255, 255, 255, .7)')
            .attr('stroke', 'black')
            .attr('stroke-width', '1');

          /* const tail = tooltip
              .selectAll('path')
              .data([undefined, undefined])
              .join('path')
              .attr('fill', 'rgba(255, 255, 255, .7)')
              .attr('stroke', 'black')
              .attr('stroke-width', '1'); */

          const text = tooltip
            .selectAll('text')
            .data([undefined, undefined])
            .join('text')
            .call((text) =>
              text
                .selectAll('tspan')
                .data([
                  `Percentile (Sum%): ${datum.percentile}`,
                  `this: ${datum.in_label.replace('about ', '~')} vs. rest: ${datum.out_label.replace('about ', '~')}`,
                ])
                .join('tspan')
                .attr('x', 0)
                .attr('y', (_, i) => `${i * 1.1}em`)
                .attr('font-size', 9)
                .attr('font-weight', (_, i) => (i ? null : 'bold'))
                .text((d) => d),
            );

          const {
            x,
            y,
            width: w,
            height: h,
          } = (text.node()! as SVGTextElement).getBBox();

          const boxPadding = 5;
          const yOffset = height < 100 ? 4 : 8;

          // Set bubble
          bubble
            .attr('x', -w / 2 - boxPadding)
            .attr('y', -boxPadding)
            .attr('width', w + boxPadding * 2)
            .attr('height', h + boxPadding * 2);

          text.attr('transform', `translate(${Math.min(-w / 2)},${yOffset})`);

          // Ensure stays in bound
          const left = Math.min(
            Math.max(-marginLeft + (w + boxPadding + boxPadding) / 2, d[0]),
            coords.width - (w + boxPadding + boxPadding) / 2,
          );
          const top = Math.min(
            Math.max(-marginTop, d[1] + 16),
            Math.max(2, coords.height - h - boxPadding * 2),
          );

          // const tailCenter = -w / 2 + (d[0] - left);

          /// Make the callout
          /* tail
              .attr('d', `M3 3 L3 25 L23 14 z`)
              .attr('transform', `translate(${tailCenter},0)`); */

          tooltip.attr('transform', `translate(${left},${top})`);
        });
    }

    // Add hover circles
    graphArea
      .selectAll('circle')
      .data(cells)
      .join('circle')
      .attr('cx', ([[x]]) => x)
      .attr('cy', ([[_, y]]) => y)
      .attr('r', 0)
      .attr('fill', 'currentColor')
      .attr('data-index', ([_d, _cell, i]) => i);

    // Create the tooltip container.
    const tooltip = graphArea
      .append('g')
      .attr('id', `tooltip-${this.identifierValue}`)
      .attr('pointer-events', 'none')
      .style('z-index', 1);

    const activePointers: Record<number, true> = {};

    function onPointerMoved(event: PointerEvent) {
      activePointers[event.pointerId] = true;

      if (event.isPrimary) {
        svg.classed('--pointed', true);
      }
    }

    function onPointerLeft(event: PointerEvent) {
      delete activePointers[event.pointerId];

      if (event.isPrimary) {
        svg.classed('--pointed', false);

        // Hide tooltip
        tooltip.style('display', 'none').attr('data-index', null);

        // Hide hover circle
        graphArea.selectAll('circle').attr('r', 0);
      }
    }

    this.graphTarget.after(svg.node()!);
  }

  public disconnect(): void {
    this.element.querySelector('svg.--interactive')?.remove();

    this.graphTarget.removeAttribute('hidden');
  }
}
